/**
 * The "Show prices & lead time for:" control: where the customer wants material delivered, which
 * decides the prices and lead times every list and line shows. A guest types a zip / postal code
 * and applies it; a signed-in customer with ship-to addresses picks one from a dropdown; either
 * can tick "Customer Pick Up", which shows the sales plant's address instead.
 *
 * Position: rendered in the subheaders of the catalog, search, cart, shopping-list details,
 * product details and quote details (for example
 * `Catalog/CatalogSubheader/CatalogSubheaderDesktop.tsx`). It writes to shared state only:
 * `zipCode` in `useApplication()` (`@ryerson/frontend.application`) and `customerPickup`,
 * `selectedShipToAddress` and `shippingAndBillingInformation` in `useEcommerce()`
 * (`@ryerson/frontend.e-commerce`). The screens watch those values and re-price and re-fetch
 * delivery times (for example `Catalog/CatalogItems.tsx`,
 * `ShoppingCart/Context/ShoppingCartContext.tsx`). `isValidZipCode` comes back from the
 * e-commerce provider and drives the "cannot ship to this zip code" alert.
 *
 * Side effects a change must account for:
 * - Changing the ship-to resets the checkout's `shippingAndBillingInformation` to `{}` and sets
 *   the zip code to the address's zip. Toggling pickup resets it when it holds a shipping address.
 * - With `isQuoteEdit`, ship-to and pickup changes go to the draft-quote setters passed in and
 *   mark the draft as changed (`setCanUpdateDraftQuote`); the global pickup flag, zip code and
 *   checkout information are left alone. The global `selectedShipToAddress` is still updated:
 *   `QuoteDetails/Context/QuoteDetailsContext.tsx` re-prices the draft and re-fetches its
 *   delivery dates when it changes.
 *
 * Related: `Helper/common.tsx` → `handleZipUpdate` (zip normalising and validation),
 * `getListOfShipToAddresses`, `getSalesPlantAddress`. Design notes:
 * https://ryerson.atlassian.net/wiki/spaces/WD/pages/2165374991/Lead+Times+-+Zip+Code+notes
 */
import React from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import { Button } from "@ryerson/frontend.button";
import { Typography } from "@ryerson/frontend.typography";
import { Input, Checkbox, Dropdown, Option } from "@ryerson/frontend.form-controls";
import { Flex, FlexItem } from "@ryerson/frontend.layout";
import { Alert } from "@ryerson/frontend.notification";
import { useTheme } from "@ryerson/frontend.theme";
import { useApplication } from "@ryerson/frontend.application";
import { useEcommerce } from "@ryerson/frontend.e-commerce";
import { LeadTimesZipContent } from "./LeadTimesZip.content";
import { getSalesPlantAddress, handleZipUpdate } from "@components/Helper/common";
import { getListOfShipToAddresses } from "@components/Helper/common";
import {
	formatZipCode,
	ShippingAndBillingInformation,
	ShipToAddress,
} from "@ryerson/frontend.common";

interface Props {
	isMobile?: boolean;
	dataTestIdCustomerPickup?: string;
	dataTestIdZipInput?: string;
	dataTestIdShipTo?: string;
	/**
	 * Default true: white text (and a dark gray panel on mobile); brand-coloured text otherwise.
	 */
	isDarkBackground?: boolean;
	/** Quote details edit mode: ship-to and pickup go to the draft-quote props below. */
	isQuoteEdit?: boolean;
	draftQuoteShipToAddress?: ShipToAddress;
	setDraftQuoteShipToAddress?: React.Dispatch<React.SetStateAction<ShipToAddress | undefined>>;
	draftQuoteCustomerPickUp?: boolean;
	setDraftQuoteCustomerPickUp?: React.Dispatch<React.SetStateAction<boolean>>;
	setCanUpdateDraftQuote?: React.Dispatch<React.SetStateAction<boolean>>;
	/** Called after the global pickup flag is toggled, with the new value (not in quote edit). */
	onChangeCustomerPickupCallBack?: (customerPickup: boolean) => void;
	onChangeSelectedShipToCallBack?: (selectedShipTo: ShipToAddress) => void;
	onChangeZipCode?: (zipCode: string) => void;
}

type AddIsMobile = {
	isMobile?: boolean;
};

const ZipCodeInput = styled(Input)`
	${(props: AddIsMobile) => {
		const { isMobile = false } = props;
		return css`
			${isMobile ? "width: 160px;" : "width: 120px; margin-right: 10px;"}
		`;
	}}
`;

const ZipCodeDropdownContainer = styled(Flex)`
	overflow-x: hidden;
	margin-right: 10px;
	${(props: AddIsMobile) => {
		const { isMobile = false } = props;
		return css`
			${isMobile ? "width: 100%;margin: 15px 0px 20px;" : "width: 260px;"}
		`;
	}}
`;

const ApplyButton = styled(Button)`
	${(props: AddIsMobile) => {
		const { isMobile = false } = props;
		return css`
			${isMobile ? "margin-left: 10px;" : "margin-right: 40px;width: 90px;"}
		`;
	}}
`;

const LabelItem = styled(FlexItem)`
	flex-grow: 0;
	flex-shrink: 0;
	${(props: any) => {
		type LangObj = { [key: string]: number };
		const { language, isMobile = false } = props;
		const displayWidths: LangObj = { es: 250, fr: 210, en: 194 };

		return isMobile
			? css`
					margin-right: 20px;
			  `
			: css`
					width: ${displayWidths[`${language}`]}px;
			  `;
	}}
`;

/**
 * Zip code / ship-to / pickup selector; see the file header. Enter applies a valid zip; "Location
 * updated" replaces the label for 5 s after Apply.
 */
const LeadTimesZipCode: React.FC<Props> = ({
	isMobile = false,
	dataTestIdCustomerPickup,
	dataTestIdZipInput,
	dataTestIdShipTo,
	isDarkBackground = true,
	isQuoteEdit = false,
	draftQuoteShipToAddress,
	setDraftQuoteShipToAddress,
	draftQuoteCustomerPickUp,
	setDraftQuoteCustomerPickUp,
	setCanUpdateDraftQuote,
	onChangeCustomerPickupCallBack,
	onChangeSelectedShipToCallBack,
	onChangeZipCode,
}) => {
	const { theme } = useTheme();
	const { white, darkGray, gray, secondaryBrand } = theme.colors.primary;
	const {
		localization: { language },
		user: { isLoggedIn },
		zipCode,
		setZipCode,
	} = useApplication();

	const content = LeadTimesZipContent[language];
	const [isZipCodeUpdated, setIsZipCodeUpdated] = React.useState<boolean>(false);
	const {
		customerPickup,
		setCustomerPickup,
		shipToAddresses,
		selectedShipToAddress,
		salesPlant,
		isValidZipCode,
		shippingAndBillingInformation,
		setShippingAndBillingInformation,
		setSelectedShipToAddress,
	} = useEcommerce();

	let backgroundColor = isDarkBackground ? darkGray : undefined;
	let fontColor = isDarkBackground ? white : secondaryBrand;

	const [currentSelectedShipToAddress, setCurrentSelectedShipToAddress] =
		React.useState<ShipToAddress>(
			isQuoteEdit && draftQuoteShipToAddress ? draftQuoteShipToAddress : selectedShipToAddress
		);

	// Option values are JSON-encoded ShipToAddress objects; the dropdown parses them back.
	const optionsShipTo: Array<Option> = getListOfShipToAddresses(shipToAddresses);
	const [headerZipCode, setHeaderZipCode] = React.useState<string>(zipCode);
	const [isValidZip, setIsValidZip] = React.useState<boolean>(false);
	const [custPickUp, setCustPickUp] = React.useState<boolean>(
		isQuoteEdit ? !!draftQuoteCustomerPickUp : customerPickup
	);

	React.useEffect(() => {
		if (isQuoteEdit && draftQuoteCustomerPickUp && !custPickUp) {
			setCustPickUp(draftQuoteCustomerPickUp);
		}
	}, [draftQuoteCustomerPickUp]);

	// sets and applies state in app context
	const onClickApplyZipCode = () => {
		setIsZipCodeUpdated(true);
		setZipCode(headerZipCode);
		onChangeZipCode && onChangeZipCode(headerZipCode);
	};

	React.useEffect(() => {
		if (zipCode !== headerZipCode) {
			setHeaderZipCode(zipCode);
		}
	}, [zipCode]);

	const handleKeypress = (e: any) => {
		if (e.keyCode === 13 && isValidZip) {
			onClickApplyZipCode();
		}
	};

	const onShipToAddressChange = (shipToAddress: ShipToAddress) => {
		if (!isQuoteEdit) {
			setSelectedShipToAddress(shipToAddress);
			setCurrentSelectedShipToAddress(shipToAddress);
			setShippingAndBillingInformation({} as ShippingAndBillingInformation);
			// Compares the part before any "-" (the 5-digit part of a ZIP+4) with the current zip.
			if (shipToAddress.zipCode.replace(/-.*/, "") !== zipCode) {
				setZipCode(formatZipCode(shipToAddress.zipCode));
			}
			onChangeSelectedShipToCallBack && onChangeSelectedShipToCallBack(shipToAddress);
		} else {
			setSelectedShipToAddress(shipToAddress);
			setCurrentSelectedShipToAddress(shipToAddress);
			setDraftQuoteShipToAddress && setDraftQuoteShipToAddress(shipToAddress);
			setCanUpdateDraftQuote && setCanUpdateDraftQuote(true);
		}
	};
	const onChangeHandleZip = (zipCode: string) => {
		handleZipUpdate(zipCode, setHeaderZipCode, setIsValidZip);
	};
	const onChangeCustomerPickup = () => {
		if (isQuoteEdit) {
			setCustPickUp(!custPickUp);
			setDraftQuoteCustomerPickUp && setDraftQuoteCustomerPickUp(!draftQuoteCustomerPickUp);
			setCanUpdateDraftQuote && setCanUpdateDraftQuote(true);
		} else {
			setCustPickUp(!custPickUp);
			setCustomerPickup(!customerPickup);
			if (
				"shippingInformation" in shippingAndBillingInformation &&
				"shippingAddress" in shippingAndBillingInformation.shippingInformation &&
				Object.values(
					shippingAndBillingInformation.shippingInformation.shippingAddress
				).some((entry) => !!entry)
			) {
				setShippingAndBillingInformation({} as ShippingAndBillingInformation);
			}
			onChangeCustomerPickupCallBack && onChangeCustomerPickupCallBack(!customerPickup);
		}
	};

	React.useEffect(() => {
		if (isZipCodeUpdated) {
			const timer = setTimeout(() => {
				setIsZipCodeUpdated(false);
			}, 5000);
			return () => clearTimeout(timer);
		}
	}, [isZipCodeUpdated]);

	return (
		<Flex
			className="outer-wrapper-lead-times-component"
			direction={"column"}
			css={
				isMobile
					? css`
							background-color: ${backgroundColor};
							padding: 15px 20px 20px;
					  `
					: undefined
			}
		>
			{!isValidZipCode && (
				<Flex css={{ marginLeft: "-10px" }}>
					<Alert size="sm" severity="error">
						<Typography
							size="sm"
							type={isMobile && !isDarkBackground ? "primary" : "tertiary"}
						>
							{content.validZipCode}
						</Typography>
					</Alert>
				</Flex>
			)}
			<Flex
				direction={isMobile ? "column" : undefined}
				alignItems={isMobile ? "flex-start" : "center"}
			>
				{isZipCodeUpdated ? (
					<LabelItem
						className="lead-time-label-area"
						css={isMobile ? { margin: -11 } : undefined}
						{...{ isMobile, language }}
					>
						<Alert severity="success" size={isMobile ? "md" : "sm"}>
							<Typography size="sm" variant="span" color={fontColor}>
								{content.locationUpdated}
							</Typography>
						</Alert>
					</LabelItem>
				) : (
					<LabelItem {...{ language }}>
						<Typography size={isMobile ? "md" : "sm"} variant="span" color={fontColor}>
							{content.showPricesAndLeadTimes}
						</Typography>
					</LabelItem>
				)}
				{isLoggedIn && optionsShipTo?.length > 0 ? (
					!custPickUp ? (
						<ZipCodeDropdownContainer {...{ isMobile }}>
							<Dropdown
								dataTestId={dataTestIdShipTo}
								shape="rounded"
								options={optionsShipTo}
								onChange={(e) => onShipToAddressChange(JSON.parse(e))}
								language={language}
								value={JSON.stringify(currentSelectedShipToAddress)}
								sizeMobile="sm"
							/>
						</ZipCodeDropdownContainer>
					) : (
						<ZipCodeDropdownContainer alignItems="center" {...{ isMobile }}>
							<Typography
								size={isMobile ? "md" : "sm"}
								variant="span"
								color={fontColor}
							>
								{getSalesPlantAddress(salesPlant, language)}
							</Typography>
						</ZipCodeDropdownContainer>
					)
				) : custPickUp ? (
					<ZipCodeDropdownContainer alignItems="center" {...{ isMobile }}>
						<Typography size={isMobile ? "md" : "sm"} variant="span" color={fontColor}>
							{getSalesPlantAddress(salesPlant, language)}
						</Typography>
					</ZipCodeDropdownContainer>
				) : (
					<Flex
						className="zip-code-input-container"
						alignItems="center"
						css={
							isMobile
								? css`
										padding: 12px 0px 13px;
								  `
								: undefined
						}
					>
						<FlexItem
							css={css`
								${isMobile && "width: 160px"};
							`}
						>
							<ZipCodeInput
								isMobile={isMobile}
								dataTestId={dataTestIdZipInput}
								value={headerZipCode}
								inputType="text"
								shape="rounded"
								onChange={(e) => {
									onChangeHandleZip(e.target.value);
								}}
								language={language}
								onKeyDown={handleKeypress}
								size="md"
								sizeMobile="sm"
							/>
						</FlexItem>
						<FlexItem
							css={css`
								${isMobile && "width: 100px"};
							`}
						>
							<ApplyButton
								isMobile={isMobile}
								onClick={() => onClickApplyZipCode()}
								label={content.apply}
								shape="rounded"
								size={isMobile ? "xs" : "sm"}
								type="secondary"
								background={isMobile ? gray : white}
								variant="outline"
								paddingType="relaxed"
								fullwidth={isMobile ? true : false}
								disabled={!isValidZip}
								dataTestId="apply"
							/>
						</FlexItem>
					</Flex>
				)}
				<Checkbox
					value={content.customerPickUp}
					onChange={onChangeCustomerPickup}
					type={isDarkBackground ? "tertiary" : "primary"}
					checked={custPickUp}
					size={isMobile ? "md" : "sm"}
					id={dataTestIdCustomerPickup}
					dataTestId={dataTestIdCustomerPickup}
					language={language}
				/>
			</Flex>
		</Flex>
	);
};

export default LeadTimesZipCode;
